/**
 * lib/cards/render-layout.js — JSON-driven layout renderer.
 *
 * Chunk 1 (Apr 24 2026): PDF renderer only. Called by the refactored
 * template-minimal.js and template-premium.js after they resolve
 * their knobs, build a layout object, and pre-render any procedural
 * background (wave band, mountain range).
 *
 * Chunk 2 will add a parallel Konva renderer consuming the same
 * layout shape.
 *
 * Content resolution pattern:
 *   The caller resolves all runtime content (images, QR codes,
 *   placeholder text) into a content map keyed by element id. The
 *   renderer doesn't call loadImageAsDataUrl or generateQR — those
 *   are async and the caller already did them.
 *
 * Signature:
 *   await renderLayoutTo(pdf, layout, contentMap, context)
 *
 * Where contentMap is:
 *   {
 *     el_staff_photo: { imageData, width, height, fallback },
 *     el_logo:        { imageData, width, height, fallback },
 *     el_name:        { text },
 *     el_role:        { text },
 *     el_qr:          { qrDataUrl },
 *     el_qr_caption:  { text },  // static_text element with literal
 *     el_banner:      { imageData, width, height, ... },
 *     el_footer_text: { text },
 *     ...
 *   }
 *
 * Static elements (static_rect, static_divider) don't need content.
 */

import {
  renderBackground,
  renderRectTo,
  renderDividerTo,
  renderTextTo,
  renderImageTo,
  renderQRTo,
  sortByZ,
} from './layout-renderers.js';

/**
 * Render a layout JSON into the supplied jsPDF instance.
 *
 * @param {jsPDF}   pdf         — target jsPDF doc
 * @param {object}  layout      — parsed layout JSON (see spec §3.2)
 * @param {object}  contentMap  — element-id-keyed content
 * @param {object}  context     — { brandColor, tenant, staff }
 * @returns {Promise<void>}
 */
export async function renderLayoutTo(pdf, layout, contentMap, context) {
  if (!layout) throw new Error('renderLayoutTo: layout is required');
  if (!Array.isArray(layout.elements)) {
    throw new Error('renderLayoutTo: layout.elements must be an array');
  }

  // Only Chunk 1 renders background here. Template-level procedural
  // backgrounds (wave band, mountain range) are painted by the calling
  // template BEFORE renderLayoutTo runs, because they're not express-
  // ible as declarative JSON elements.
  //
  // To avoid double-painting the white background over an already-
  // drawn procedural banner, the caller passes skipBackground=true
  // in context when appropriate.
  if (!context?.skipBackground) {
    renderBackground(pdf, layout);
  }

  // z-order determines draw sequence — lower z first.
  const ordered = sortByZ(layout.elements);

  for (const element of ordered) {
    try {
      await dispatchElement(pdf, element, contentMap?.[element.id], context);
    } catch (e) {
      console.warn(
        `[render-layout] element "${element.id}" (${element.type}) failed:`,
        e?.message || e,
      );
      // Don't abort the whole render for one bad element
    }
  }
}

/**
 * Route an element to the correct primitive. Unknown types log and skip.
 */
async function dispatchElement(pdf, element, content, context) {
  switch (element.type) {
    case 'static_rect':
      renderRectTo(pdf, element, context);
      return;

    case 'static_divider':
      renderDividerTo(pdf, element, context);
      return;

    case 'static_text':
      // Static text: literal string lives in props.text; no contentMap entry needed
      renderTextTo(
        pdf,
        element,
        { text: content?.text ?? element.props?.text ?? '' },
        context,
      );
      return;

    case 'dynamic_name':
    case 'dynamic_role':
    case 'dynamic_footer_text':
      renderTextTo(pdf, element, content || { text: '' }, context);
      return;

    case 'dynamic_photo':
    case 'dynamic_logo':
    case 'dynamic_banner':
    case 'static_image':
      renderImageTo(pdf, element, content, context);
      return;

    case 'dynamic_qr':
      renderQRTo(pdf, element, content, context);
      return;

    default:
      console.warn(`[render-layout] unknown element type: ${element.type}`);
      return;
  }
}

/**
 * Lightweight schema validation. Called by template files before
 * handing a layout to the renderer, and in Chunk 3 by the save-layout
 * API endpoint. Returns { ok: boolean, errors: string[] }.
 *
 * Rules checked:
 *   - version is present and understood
 *   - elements is an array
 *   - each element has id, type, x, y, w, h
 *   - no element extends outside card bounds
 *   - no element has w/h <= 0
 *   - QR (if present) is at least 18×18mm
 *
 * Not checked here (UI/editor job):
 *   - required elements present (photo, name, QR)
 *   - soft constraints (name font size, bleed overlaps)
 */
export function validateLayout(layout, cardW, cardH) {
  const errors = [];

  if (!layout || typeof layout !== 'object') {
    return { ok: false, errors: ['layout is not an object'] };
  }

  if (layout.version !== 1) {
    errors.push(`unsupported layout version: ${layout.version}`);
  }

  if (!Array.isArray(layout.elements)) {
    errors.push('layout.elements is not an array');
    return { ok: false, errors };
  }

  const seen = new Set();
  for (const el of layout.elements) {
    if (!el.id) {
      errors.push('element missing id');
      continue;
    }
    if (seen.has(el.id)) {
      errors.push(`duplicate element id: ${el.id}`);
    }
    seen.add(el.id);

    if (!el.type) {
      errors.push(`element ${el.id}: missing type`);
    }
    if (typeof el.x !== 'number' || typeof el.y !== 'number') {
      errors.push(`element ${el.id}: x/y must be numbers`);
    }
    if (typeof el.w !== 'number' || typeof el.h !== 'number') {
      errors.push(`element ${el.id}: w/h must be numbers`);
    }
    if (el.w <= 0 || el.h <= 0) {
      errors.push(`element ${el.id}: w/h must be > 0`);
    }
    if (el.x < 0 || el.y < 0 || el.x + el.w > cardW || el.y + el.h > cardH) {
      errors.push(
        `element ${el.id}: extends outside card bounds ` +
        `(${el.x},${el.y} ${el.w}×${el.h} vs ${cardW}×${cardH})`,
      );
    }
    if (el.type === 'dynamic_qr' && Math.min(el.w, el.h) < 18) {
      errors.push(`element ${el.id}: QR smaller than 18mm minimum`);
    }
  }

  return { ok: errors.length === 0, errors };
}
